import ErrorMessages from 'enums/ErrorMessages';
import RequestError from 'errors/RequestError';
import { failureNotification } from 'notifications';
import { Environments, getEnvironment } from 'utils/getEnvironment';
import sleep from 'utils/sleep';
import { getRedirectPathForLogin } from 'utils/loginRedirectUtils';
import { getInjectedStoreState } from 'store/injectStore';

export type AccessTokenGetter = () => Promise<string | null>;

interface FetchResponse<T = any> extends Response {
  json<P = T>(): Promise<P>;
}

export interface IRequestErrors extends Error {
  response?: FetchResponse;
}

export enum HTTPStatusCodes {
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  CONFLICT = 409,
}

const redirectToLoginStatusCodes = [HTTPStatusCodes.UNAUTHORIZED, HTTPStatusCodes.FORBIDDEN, HTTPStatusCodes.CONFLICT];

export enum FetchMethodType {
  POST = 'POST',
  PUT = 'PUT',
  GET = 'GET',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export enum FetchResponseType {
  Json = 'json',
  Blob = 'blob',
}

export enum ErrorMode {
  HideError,
  DisplayGenericMessage,
  DisplayActualMessage,
}

export interface IFetchOptions {
  headers?: Headers;
  resetDefaultHeaders?: boolean;
  method?: FetchMethodType;
  body?: any;
  statusChecker?: (res: Response, errorMode: ErrorMode) => Promise<FetchResponse<Body>>;
  errorMode?: ErrorMode;
  maxRetries?: number;
  retries?: number;
  redirectToLogin?: boolean;
  responseType?: FetchResponseType;
}

const checkStatus = async (response: FetchResponse, errorMode: ErrorMode, value: any): Promise<FetchResponse> => {
  if (response.ok) {
    return response;
  }

  throw new RequestError(
    value && (errorMode === ErrorMode.DisplayActualMessage || getEnvironment() === Environments.Staging)
      ? (typeof value === 'string'
          ? value
          : value.message || value.error || value.data?.error || value.error?.message) || response.statusText
      : '',
    response.status,
    value,
  );
};

const getBasicHeaders = () => {
  const headers = new Headers();

  headers.set('Accept', 'application/json');
  headers.set('Content-Type', 'application/json');

  const sessionToken = getInjectedStoreState()?.auth?.sessionToken;
  if (sessionToken) {
    headers.append('X-Session-Token', sessionToken);
  }

  return headers;
};

const getLastStreamMessage = (value: Uint8Array): any => {
  const decoder = new TextDecoder();
  const message = decoder.decode(value);

  const array = message.split('\n').filter((item) => item !== '');
  const lastMessage = array[array.length - 1];

  return JSON.parse(lastMessage);
};

export default class Api {
  constructor(protected readonly baseUrl?: string) {}

  protected async fetch<Body>(url: string, options?: IFetchOptions): Promise<Body> {
    const {
      headers: customHeaders,
      method = FetchMethodType.GET,
      body,
      resetDefaultHeaders,
      statusChecker = checkStatus,
      errorMode = ErrorMode.DisplayGenericMessage,
      retries = 0,
      maxRetries = 2,
      redirectToLogin = true,
      responseType = FetchResponseType.Json,
    } = options || {};

    const headers = resetDefaultHeaders ? new Headers() : getBasicHeaders();

    if (customHeaders) {
      customHeaders.forEach((value: string, header: string) => {
        headers.set(header, value);
      });
    }

    try {
      const response = await fetch(this.baseUrl ? `${this.baseUrl}${url}` : url, {
        method,
        headers,
        body: body instanceof FormData ? body : JSON.stringify(body),
      });

      if (responseType === FetchResponseType.Blob) {
        if (!response.ok) {
          let value = null;
          try {
            value = await response.json();
          } catch (err: unknown) {
            value = await response.text();
            console.warn(`Value is not JSON: ${value}`);
          }
          await statusChecker((response as unknown) as FetchResponse, errorMode, value);
        }
        return ((await response.blob()) as unknown) as Body;
      }

      let value = null;

      // Handle HTTP 204 No Content responses
      if (response.status === 204) {
        await statusChecker(response, errorMode, null);
        return (null as unknown) as Body;
      }

      try {
        value = await response.json();
      } catch (err: unknown) {
        value = await response.text();
        console.warn(`Value is not JSON: ${value}`);
      }

      await statusChecker(response, errorMode, value);

      return value;
    } catch (err: unknown) {
      const { responseStatus: statusCode } = err as { responseStatus?: number };
      if (statusCode && redirectToLoginStatusCodes.includes(statusCode) && redirectToLogin) {
        window.location.href = getRedirectPathForLogin(statusCode);
      } else {
        if (
          retries <= maxRetries &&
          (statusCode === undefined || statusCode === 429 || statusCode === 0 || statusCode >= 500)
        ) {
          const newOptions = { ...options, retries: retries + 1 };
          console.warn('Retrying...', { url, newOptions });
          await sleep(2 ** retries * 200); // exponential backoff
          return this.fetch(url, newOptions);
        }

        if (errorMode !== ErrorMode.HideError) {
          failureNotification((err as RequestError<unknown>).message || ErrorMessages.Default);
        }
      }
      throw err;
    }
  }

  protected async *fetchStream<Body>(url: string, options?: IFetchOptions): AsyncGenerator<Body> {
    const {
      headers: customHeaders,
      method = FetchMethodType.GET,
      body,
      resetDefaultHeaders,
      statusChecker = checkStatus,
      errorMode = ErrorMode.DisplayGenericMessage,
      retries = 0,
      maxRetries = 2,
    } = options || {};

    const headers = resetDefaultHeaders ? new Headers() : getBasicHeaders();

    if (customHeaders) {
      customHeaders.forEach((value: string, header: string) => {
        headers.set(header, value);
      });
    }

    try {
      const response = await fetch(this.baseUrl ? `${this.baseUrl}${url}` : url, {
        method,
        headers,
        body: body instanceof FormData ? body : JSON.stringify(body),
      });

      const reader = response.body!.getReader();

      let prev = await reader.read();
      let done = false;
      while (!done) {
        // eslint-disable-next-line no-await-in-loop
        const next = await reader.read();
        if (!next.done) {
          if (prev.value) {
            yield getLastStreamMessage(prev.value);
          }
          prev = next;
        }
        done = next.done;
      }

      const value = getLastStreamMessage(prev.value!);
      await statusChecker(response, errorMode, value);
      yield value;
    } catch (err: unknown) {
      const { responseStatus: statusCode } = err as { responseStatus?: number };
      if (
        retries <= maxRetries &&
        (statusCode === undefined || statusCode === 429 || statusCode === 0 || statusCode >= 500)
      ) {
        await sleep(2 ** retries * 200); // exponential backoff
        return yield* this.fetchStream(url, { ...options, retries: retries + 1 });
      }

      if (errorMode !== ErrorMode.HideError) {
        failureNotification((err as RequestError<unknown>).message || ErrorMessages.Default);
      }

      throw err;
    }
  }
}
